import { Box, Dialog, SvgIcon, Typography, useMediaQuery, Drawer, Stack } from '@mui/material';
import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import SelectResumeOptions from './SelectResumeOptions';
import { useTheme } from '@mui/material/styles';
import SelectResumeMobile from './SelectResumeMobile';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuthContext';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

const resumeImg = `${process.env.REACT_APP_HOMEPAGE}assets/resume_thumbnail_2.jpg`;

const settings = {
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 2.4,
  slidesToScroll: 2,
};

/**
 * Component for selecting and managing resume options within a chat interface.
 *
 * @param {Function} onSet - Callback function to set the selected resume.
 * @param {Array} resumeData - List of resumes available for selection.
 * @param {string} tab - Current tab identifier in the UI.
 * @param {Object} job - Job object containing details of the job application.
 * @param {Array} answers - List of answers related to the application process.
 * @param {Array} messages - Array of messages for chat interaction.
 * @param {number} totalExpectedCompensation - Expected compensation for the job.
 * @param {boolean} disableUpdate - Flag to disable updates.
 * @param {Object} candidateDetails - Details of the candidate.
 * @param {boolean} resumeSelected - Indicates if a resume has been selected.
 * @param {Function} fetchResumeData - Function to fetch resume data.
 *
 * @returns {JSX.Element} The rendered component for selecting resumes.
 */

const ChatResumeOptions = ({
  onSet,
  resumeData = [],
  tab,
  job,
  answers,
  messages,
  totalExpectedCompensation,
  disableUpdate,
  candidateDetails,
  resumeSelected,
  fetchResumeData,
  currentFixed,
  currentVariable,
  currentPerks,
  expectedHourlyRate,
  totalCurrentCTC
}) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // const [open, setOpen] = useState(false);
  const [resumeIndex, setIndex] = useState(0);
  const [chosenResume, setChosenResume] = useState(null);
  const { setJobApply } = useAuth();
  const navigate = useNavigate();

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const selectResumeDialogOpen = dialogIds.includes("selectResumeDialog");


  useEffect(() => {
    if (selectResumeDialogOpen) localStorage.setItem('openChatBot', true);
    else localStorage.removeItem('openChatBot');
  }, [selectResumeDialogOpen]);

  /**
   * Opens the OTP verification dialog for the given resume item and index.
   * @param {Object} item - Resume item to open OTP dialog for.
   * @param {number} index - Index of the resume item in the list.
   */
  const handleOpenOtpDialog = (item, index) => {
    setChosenResume(item);
    setIndex(index);
    handleOpenSelectResumeDialog();
  };

  /**
   * Closes the chatbot resume options dialog and resets the
   * openSelectResume state in the job application context.
   */
  const handleClose = () => {
    handleCloseSelectResumeDialog();
    setJobApply((prev) => ({ ...prev, openSelectResume: false }));
  }

  /**
   * Handles the logic to select a resume and set the selected resume
   * in the job application context. The function also closes the chatbot
   * resume options dialog and resets the openSelectResume state in the
   * job application context.
   * @param {Object} resume - Resume to be selected.
   * @param {number} index - Index of the resume in the list.
   */
  const handleSelect = (resume, index) => {
    handleCloseSelectResumeDialog();
    setJobApply((prev) => ({ ...prev, openSelectResume: false }));
    onSet(resume, index);
  };

  const commonProps = {
    onSelect: handleSelect,
    resumeData,
    tab,
    job,
    index: resumeIndex,
    answers,
    chosenResume,
    messages,
    candidateDetails,
    totalExpectedCompensation,
    disableUpdate,
    resumeSelected,
    fetchResumeData,
    currentFixed,
    currentVariable,
    currentPerks,
    expectedHourlyRate,
    totalCurrentCTC
  };

  /**
   * Handles the logic to navigate to the create-update-resume page
   * when the user clicks on the "Create a new resume" button.
   * If the user does not have a master resume, this function will
   * navigate to the create-update-resume page with the isMaster flag
   * set to true. If the user has a master resume, this function will
   * navigate to the create-update-resume page with the createNewResume
   * flag set to true and the totalResume flag set to the total number
   * of resumes the user has.
   */
  const handleCreateNewResume = () => {
    setJobApply({
      job, openChatBot: true, answer: answers, tab, messages, candidateDetails, totalExpectedCompensation, disableUpdate,
      partTimeContinueFlag: true, continueFlag: true, currentFixed,
      currentVariable,
      currentPerks, hourlyRate: expectedHourlyRate, totalCurrentCTC,
      index: resumeData?.length,
    });
    localStorage.setItem('openChatBot', true);
    const isResume = resumeData?.find(item => item.title === 'Master Resume');
    if (!isResume) navigate('/create-update-resume', { state: { isMaster: true } });
    else navigate('/create-update-resume', { state: { totalResume: resumeData?.length, createNewResume: true } });
  };

  const handleOpenSelectResumeDialog = () => {
    openDialog('selectResumeDialog');
  };
  const handleCloseSelectResumeDialog = () => {
    closeDialog('selectResumeDialog');
  };

  return (
    <Box className="select-resume-option-container"
      sx={{
        pointerEvents: resumeSelected ? 'none' : 'auto',
        opacity: resumeSelected ? 0.5 : 1,
        overflow: 'visible',
        position: 'relative',
      }}>
      <Slider {...settings} arrows={(resumeData?.length + 1) >= settings.slidesToShow && !isMobile}>
        {resumeData?.map((item, index) => (
          <Box key={index} className="resume-option"
            onClick={() => handleOpenOtpDialog(item, index)}>
            <img alt="resume" src={resumeImg} />
            <Box
              className="select-resume-action"
              sx={{
                cursor: 'pointer',
                WebkitTapHighlightColor: "transparent", // removes gray flash on iOS
                userSelect: "none",
              }}
            >
              <SvgIcon className="jn-chat-icon" sx={{ pointerEvents: "none" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.09274 6.14966C2.38619 7.75435 0.891638 9.09068 0.727274 9.09068C0.325819 9.09068 0 8.76487 0 8.36342V0.727254C0 0.32581 0.325819 0 0.727274 0H8.72729C9.12875 0 9.45456 0.32581 9.45456 0.727254C9.45456 0.897795 7.95347 2.43121 6.19601 4.13917L9.02911 6.97219C9.59711 7.53981 9.59711 8.46087 9.02911 9.02886C8.74475 9.31285 8.37274 9.45503 8.00038 9.45503C7.62802 9.45503 7.25601 9.31285 6.97201 9.02886L4.09274 6.14966ZM8.72729 24.0001H0.727273C0.325818 24.0001 0 23.6743 0 23.2728V15.6367C0 15.2352 0.325818 14.9094 0.727273 14.9094C0.891637 14.9094 2.38619 16.2457 4.0931 17.8504L6.97165 14.972C7.53965 14.4036 8.46038 14.4036 9.02875 14.972C9.59675 15.5396 9.59675 16.4606 9.02875 17.0286L6.19601 19.8613C7.95347 21.5689 9.45457 23.1023 9.45457 23.2728C9.45457 23.6743 9.12875 24.0001 8.72729 24.0001ZM23.9994 23.2728V15.6367C23.9994 15.2352 23.6736 14.9094 23.2721 14.9094C23.1078 14.9094 21.6132 16.2457 19.9067 17.8504L17.0281 14.972C16.4598 14.4036 15.539 14.4036 14.971 14.972C14.403 15.5399 14.403 16.461 14.971 17.0286L17.8038 19.8613C16.0459 21.5689 14.5448 23.1023 14.5448 23.2728C14.5448 23.6743 14.8707 24.0001 15.2721 24.0001H23.2721C23.6736 24.0001 23.9994 23.6743 23.9994 23.2728ZM23.2727 0C23.6742 0 24 0.32581 24 0.727254V8.36342C24 8.76487 23.6742 9.09068 23.2727 9.09068C23.1084 9.09068 21.6138 7.75435 19.9073 6.14966L17.0287 9.02813C16.7443 9.31213 16.3723 9.4543 16 9.4543C15.6276 9.4543 15.2556 9.31213 14.9713 9.02813C14.4033 8.46051 14.4033 7.53944 14.9713 6.97146L17.804 4.1388C16.0465 2.43121 14.5454 0.897795 14.5454 0.727254C14.5454 0.32581 14.8713 0 15.2727 0H23.2727Z"
                    fill="white"
                  />
                </svg>
              </SvgIcon>
            </Box>
            <Box className="resume-title"
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(item, index);
              }}
              sx={{ cursor: 'pointer' }}>
              <Typography>{item?.link ? 'Uploaded Resume' : item?.title || 'NA'}</Typography>
            </Box>
          </Box>
        ))}
        {resumeData?.length < 5 && (

          <Box className="resume-option" onClick={handleCreateNewResume} >
            <Box className="create-new-resume-slide" sx={{ cursor: 'pointer' }}>
              <Stack justifyContent={'center'} alignItems={'center'} spacing={1}>
                <SvgIcon sx={{ pointerEvents: "none" }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="74" height="88" viewBox="0 0 74 88" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.35059 0.196289C3.93231 0.196289 0.350586 3.77801 0.350586 8.19629V74.1963C0.350586 78.6146 3.93231 82.1963 8.35059 82.1963H41.0889C40.6326 81.5543 40.2038 80.8927 39.8271 80.1963H10.3506C5.93231 80.1963 2.35059 76.6146 2.35059 72.1963V10.1963C2.35059 5.77801 5.93231 2.19629 10.3506 2.19629H52.3506C56.7689 2.19629 60.3506 5.77801 60.3506 10.1963V51.9912C61.0299 52.1154 61.6978 52.2704 62.3506 52.4638V8.19628C62.3506 3.778 58.7689 0.196289 54.3506 0.196289H8.35059ZM15.5127 48.0078V50.0059H47.1904V48.0078H15.5127ZM15.5127 57.8173V55.8193H44.915C44.1405 56.4295 43.418 57.0981 42.7451 57.8173H15.5127ZM25.3252 29.5264V29.7432V35.7705H37.3779V29.5264H35.3818V33.7725H27.3213V29.5264H25.3252ZM27.8389 26.7021C27.8389 24.7727 29.4211 23.1904 31.3506 23.1904C33.2801 23.1904 34.8643 24.7727 34.8643 26.7021C34.8643 28.6316 33.2799 30.2139 31.3506 30.2139C29.4212 30.2139 27.8389 28.6316 27.8389 26.7021ZM32.8662 26.7021C32.8662 25.8509 32.2018 25.1865 31.3506 25.1865C30.4993 25.1865 29.835 25.8509 29.835 26.7021C29.835 27.5534 30.4991 28.2178 31.3506 28.2178C32.2021 28.2178 32.8662 27.5534 32.8662 26.7021ZM31.3506 16.7646C24.3397 16.7646 18.6357 22.4686 18.6357 29.4795C18.6357 36.4904 24.3397 42.1943 31.3506 42.1943C38.3615 42.1943 44.0674 36.4904 44.0674 29.4795C44.0674 22.4686 38.3615 16.7646 31.3506 16.7646ZM31.3506 18.7607C37.2822 18.7607 42.0693 23.5479 42.0693 29.4795C42.0693 35.4111 37.2822 40.1982 31.3506 40.1982C25.419 40.1982 20.6338 35.4111 20.6338 29.4795C20.6338 23.5479 25.419 18.7607 31.3506 18.7607ZM15.5127 65.628V63.6299H38.9795C38.7106 64.2782 38.4842 64.9467 38.2861 65.628H15.5127ZM50.4399 55.5089C52.4767 54.6653 54.6597 54.2313 56.8643 54.2314C59.0687 54.2315 61.2515 54.6658 63.288 55.5095C65.3246 56.3531 67.175 57.5897 68.7337 59.1486C70.2923 60.7074 71.5287 62.558 72.3721 64.5947C73.2156 66.6313 73.6496 68.8142 73.6494 71.0186C73.6492 75.4703 71.8808 79.7395 68.733 82.8873C65.5852 86.035 61.3159 87.8035 56.8643 87.8036C54.6599 87.8038 52.477 87.3699 50.4404 86.5264C48.4037 85.683 46.5531 84.4466 44.9943 82.888C43.4354 81.3294 42.1989 79.4789 41.3552 77.4423C40.5115 75.4058 40.0772 73.223 40.0771 71.0186C40.077 68.814 40.5111 66.631 41.3546 64.5942C42.1982 62.5574 43.4347 60.7068 44.9936 59.1479C46.5524 57.589 48.4031 56.3525 50.4399 55.5089ZM57.8643 70.0165V63.5165H55.8643V70.0165H49.3643V72.0165H55.8643V78.5165H57.8643V72.0165H64.3643V70.0165H57.8643Z" fill="#1575C6" />
                  </svg>
                </SvgIcon>
                <Typography>Create new resume and select</Typography>
              </Stack>
            </Box>
          </Box>
        )}

      </Slider>
      {/* select resume options */}
      {isMobile ? (
        <Drawer
          anchor="right"
          open={selectResumeDialogOpen}
          onClose={handleClose}
          className="mobile-bottomsheet-dialog action-dialog-card external-job-app-dialog"
        >
          <SelectResumeMobile {...commonProps} onClose={handleClose} />
        </Drawer>
      ) : (
        <Dialog
          open={selectResumeDialogOpen}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="select-resumes-dialog"
        >
          <SelectResumeOptions {...commonProps} onClose={handleClose} />
        </Dialog>
      )}
    </Box>
  );
};

export default ChatResumeOptions;
